import { AppFieldsType, WorkflowStepType } from "@/constants/workflows/workflowCreate";
import { AppField, FieldsResponse } from "@/types/WorkflowCreate/fields";
import { App } from "@/types/applications";

/**
 * Transforms the data into a unified field array structure.
 * Each application (e.g. camp_minder, google_sheets) becomes a field entry.
 */
export const getAppFieldsAccordingToType = (
  data: Record<string, FieldsResponse>,
  fieldType: AppFieldsType,
  applications: App[]
): AppField[] => {
  const getStepOrder = (key: string) => {
    const [stepIndexRaw] = key.split(";")
    const parsed = Number(stepIndexRaw)
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
  }

  return Object.entries(data)
    .slice()
    .sort(([aKey], [bKey]) => getStepOrder(aKey) - getStepOrder(bKey))
    .map(([key, value], stepPositionIndex) => {
    // Extract readable app name (after semicolon)
    const [stepIndex, stepName] = key.split(";");
    const name = stepName === WorkflowStepType.TRANSFORMATION ? WorkflowStepType.TRANSFORMATION : stepName;
    const app = applications.find((a) => a.name === name);
    const displayName = app ? app.displayName : stepName === WorkflowStepType.TRANSFORMATION ? 'Transformation Step' : '';

    // Get fields of requested type
    const fields = value[fieldType] || [];

    return {
      code: name,
      codeChain: name,
      order: Number(stepIndex),
      type: "object",
      description: displayName,
      // Use step position (1..N) so deleted steps don't create gaps.
      displayName: `Step ${stepPositionIndex + 1}`,
      fields,
    };
  });
}

/**
 * Gets iterable fields (ARRAY type) from the provided data.
 * @param {Record<string, FieldsResponse>} data
 * @param {App[]} applications
 * @returns {AppField[]}
 */
export const getIterableFields = (
  data: Record<string, FieldsResponse>,
  applications: App[]
): AppField[] => {
  const getStepOrder = (key: string) => {
    const [stepIndexRaw] = key.split(";")
    const parsed = Number(stepIndexRaw)
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
  }

  return Object.entries(data)
    .slice()
    .sort(([aKey], [bKey]) => getStepOrder(aKey) - getStepOrder(bKey))
    .map(([key, value], stepPositionIndex) => {
    const [stepIndex, appName] = key.split(";");
    const app = applications.find((a) => a.name === appName);

    // Filter only ARRAY type fields from responseFields
    const iterableFields = (value.responseFields || [])
      .filter((field: AppField) => field.type === "ARRAY")
      .map((field: AppField, index: number) => ({
        code: field.code,
        codeChain: field.code,
        order: index,
        displayName: field.displayName,
        type: field.type,
        fields: [],
        description: field.description || "",
      }));

    return {
      code: appName,
      codeChain: appName,
      order: Number(stepIndex),
      type: "object",
      description: app?.displayName || "",
      // Use step position (1..N) so deleted steps don't create gaps.
      displayName: `Step ${stepPositionIndex + 1}`,
      fields: iterableFields,
    };
  });
};
